<template>
  <div
    class="table-pagination-v2"
    :class="{ 'table-pagination-v2--rtl': isRTL }"
  >
    <nav
      v-if="totalPages > 1"
      class="table-pagination-v2__nav"
      :aria-label="$t('TABLE_V2.PAGINATION.LABEL')"
    >
      <button
        type="button"
        class="paginate-btn"
        :class="{
          'paginate-btn--disabled': isFirstPage || disabled || loading,
        }"
        :disabled="isFirstPage || disabled || loading"
        :aria-label="$t('TABLE_V2.PAGINATION.FIRST')"
        @click="goToPage(1)"
      >
        <v2-icon icon="keyboard_double_arrow_left" size="16" />
      </button>

      <button
        type="button"
        class="paginate-btn"
        :class="{
          'paginate-btn--disabled': isFirstPage || disabled || loading,
        }"
        :disabled="isFirstPage || disabled || loading"
        :aria-label="$t('TABLE_V2.PAGINATION.PREVIOUS')"
        @click="goToPage(currentPage - 1)"
      >
        <v2-icon icon="keyboard_arrow_left" size="16" />
      </button>

      <template v-for="(page, index) in visiblePages">
        <span
          v-if="page === '...'"
          :key="`ellipsis-${index}`"
          class="paginate-btn paginate-btn--ellipsis"
          aria-hidden="true"
        >
          &hellip;
        </span>
        <button
          v-else
          :key="`page-${page}`"
          type="button"
          class="paginate-btn"
          :class="{ 'paginate-btn--active': page === currentPage }"
          :aria-label="$t('TABLE_V2.PAGINATION.PAGE', { page })"
          :aria-current="page === currentPage ? 'page' : undefined"
          :disabled="disabled || loading"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
      </template>

      <button
        type="button"
        class="paginate-btn"
        :class="{ 'paginate-btn--disabled': isLastPage || disabled || loading }"
        :disabled="isLastPage || disabled || loading"
        :aria-label="$t('TABLE_V2.PAGINATION.NEXT')"
        @click="goToPage(currentPage + 1)"
      >
        <v2-icon icon="keyboard_arrow_right" size="16" />
      </button>

      <button
        type="button"
        class="paginate-btn"
        :class="{ 'paginate-btn--disabled': isLastPage || disabled || loading }"
        :disabled="isLastPage || disabled || loading"
        :aria-label="$t('TABLE_V2.PAGINATION.LAST')"
        @click="goToPage(totalPages)"
      >
        <v2-icon icon="keyboard_double_arrow_right" size="16" />
      </button>
    </nav>

    <span class="table-pagination-v2__summary">{{ summaryText }}</span>
  </div>
</template>

<script>
import V2Icon from 'v2/components/Icon.vue';

export default {
  name: 'TablePaginationV2',
  components: { V2Icon },
  props: {
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isRTL: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    rangeStart() {
      if (!this.totalItems) return 0;
      return (this.currentPage - 1) * this.pageSize + 1;
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.pageSize, this.totalItems);
    },
    summaryText() {
      return this.$t('TABLE_V2.PAGINATION.SHOWING', {
        start: this.rangeStart,
        end: this.rangeEnd,
        total: this.totalItems,
      });
    },
    isFirstPage() {
      return this.currentPage <= 1;
    },
    isLastPage() {
      return this.currentPage >= this.totalPages;
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      const left = Math.max(1, Math.min(current - 1, total - 2));
      const right = Math.min(total, Math.max(current + 1, 3));

      let last = 0;
      for (let i = 1; i <= total; i += 1) {
        if (i === 1 || i === total || (i >= left && i <= right)) {
          if (last && i - last > 1) pages.push('...');
          pages.push(i);
          last = i;
        }
      }
      return pages;
    },
  },
  methods: {
    goToPage(page) {
      if (page < 1 || page > this.totalPages) return;
      if (page === this.currentPage) return;
      if (this.disabled || this.loading) return;

      this.$emit('update:currentPage', page);
      this.$emit('page-change', page);
    },
  },
};
</script>

<style lang="scss" scoped>
.table-pagination-v2 {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 32px;
  margin-top: 12px;
}

.table-pagination-v2__summary {
  margin-inline-start: auto;
  font-size: 13px;
  font-weight: 400;
  font-family: 'Open Sans', sans-serif;
  color: theme('colors.neutral.600');
  white-space: nowrap;
}

.table-pagination-v2--rtl {
  direction: rtl;

  .paginate-btn {
    ::v-deep .material-symbols-outlined,
    ::v-deep .v2-icon-svg {
      transform: scaleX(-1);
    }
  }
}

.table-pagination-v2__nav {
  display: flex;
  align-items: center;
  gap: 8px;
}

.paginate-btn {
  display: flex;
  min-width: 32px;
  height: 32px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: 0 6px;
  border-radius: 8px;
  border: 1px solid theme('colors.neutral.100');
  background: theme('colors.white');
  color: theme('colors.neutral.600');
  font-size: 13px;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  line-height: 1;
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    color 0.15s ease;
  outline: none;
  user-select: none;

  &:hover:not(.paginate-btn--active):not(.paginate-btn--disabled):not(
      .paginate-btn--ellipsis
    ) {
    border-color: theme('colors.brand.DEFAULT');
    color: theme('colors.brand.DEFAULT');
  }

  &:focus-visible:not(.paginate-btn--active):not(.paginate-btn--disabled) {
    border-color: theme('colors.brand.DEFAULT');
    box-shadow: 0 0 0 2px rgba(42, 187, 127, 0.25);
  }

  &--active {
    background: theme('colors.brand.DEFAULT');
    border-color: theme('colors.brand.DEFAULT');
    color: theme('colors.white');
    cursor: default;

    &:hover {
      background: theme('colors.brand.DEFAULT');
      border-color: theme('colors.brand.DEFAULT');
      color: theme('colors.white');
    }
  }

  &--disabled {
    color: theme('colors.neutral.400');
    cursor: not-allowed;
    opacity: 1;

    svg {
      color: theme('colors.neutral.400');
    }
  }

  &--ellipsis {
    cursor: default;
    pointer-events: none;

    &:hover {
      border-color: theme('colors.neutral.100');
      color: theme('colors.neutral.600');
    }
  }
}
</style>
